import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataProvider,
  Filtering,
  Pagination,
  QDataTable,
  QEmptyState,
  QSelectItem,
  QStack,
  Sorting,
  useCurrentUser,
  CumulativeSearchParamsProvider,
  SavedViews,
} from '@qualio/ui-components';
import eventsApi from '../../api/events.api';
import {
  EventTag,
  EventType,
  ListQualityEvent,
  Product,
  ProbabilityType,
  QualityEventsGet,
  RelatedRecordRef,
  RootCause,
  SeverityType,
  User,
} from '../../types';
import { getUtcEndOfDay, getUtcStartOfDay } from '../../utils/datetimeUtils';
import { useEventMetadata, useEventTypes, useTags, useUsers } from '../../hooks';
import StatusFilter from './components/StatusFilter';
import EventTypeFilter from './components/EventTypeFilter';
import ActiveEventTypeFilter from './components/ActiveEventTypeFilter';
import { useProducts } from '../../hooks/useProducts';
import ActiveProductFilter from './components/ActiveProductFilter';
import ProductFilter from './components/ProductFilter';
import { useRootCauses } from '../../hooks/useRootCauses';
import ActiveRootCauseFilter from './components/ActiveRootCauseFilter';
import RootCauseFilter from './components/RootCauseFilter';
import ActiveUserFilter from './components/ActiveUserFilter';
import UserFilter from './components/UserFilter';
import ActiveTagFilter from './components/ActiveTagFilter';
import TagFilter from './components/TagFilter';
import ActiveDueFilter from './components/ActiveDueFilter';
import DueFilter from './components/DueFilter';
import ActiveStatusFilter from './components/ActiveStatusFilter';
import ActiveDateFilter from './components/ActiveDateFilter';
import DateFilter from '../shared/DateFilter/DateFilter';
import ActiveSeverityFilter from './components/ActiveSeverityFilter';
import SeverityFilter from './components/SeverityFilter';
import ActiveProbabilityFilter from './components/ActiveProbabilityFilter';
import ProbabilityFilter from './components/ProbabilityFilter';
import ActiveRelatedRecordFilter from './components/ActiveRelatedRecordFilter';
import RelatedRecordsFilter from './components/RelatedRecordsFilter';
import { eventsListSavedViewsTableKey, eventsTablePersistanceKey } from '../../types/common/constants';
import { useFlags } from '../../external/LaunchDarklyAdapter';
import { decodeQri } from '../../utils/relatedRecords';
import { filterTags } from '../../utils/eventUtils';
import { filterActiveUsers } from '../../utils/userUtils';
import { FilterByCreatedDate, FilterByDueDate, FilterByResolvedDate } from '../../displayStrings';
import { medtechApi } from '../../api';
import { getEventTableColumns } from './columns';

const SEARCH_TOTAL_ITEMS_CAP = 10000;

const EventTable = () => {
  const { t } = useTranslation();
  const isMultiSelectEventsListEnabled = useFlags('multiSelectEventsList');
  const isAdditionalEventListFiltersEnabled = useFlags('additionalEventListFilters');
  const isEventCollaboratorsEnabled = useFlags('eventCollaborators');
  const isSavedViewsEnabled = useFlags('listSavedViews');
  const isConfigurableColumnsEnabled = useFlags('configurableColumnsEventList');
  const { localiseDate, companyId, tz, userId } = useCurrentUser();
  const { Provider: PaginationProvider } = Pagination.useLimitOffset();
  const { data: fetchedEventTypes, isLoading: isLoadingEventTypes } = useEventTypes();
  const { data: fetchedProducts, isLoading: isLoadingProducts } = useProducts();
  const { data: fetchedRootCauses, isLoading: isLoadingRootCauses } = useRootCauses();
  const { data: fetchedUsers, isLoading: isLoadingUsers } = useUsers();
  const { data: fetchedTags, isLoading: isLoadingTags } = useTags();
  const { data: fetchedEventMetadata, isLoading: isLoadingEventMetadata } = useEventMetadata();

  const statusValueLabelMap: Record<string, string> = useMemo(
    () => ({
      open: t('common.labels.open'),
      closed: t('common.labels.closed'),
      imported: t('common.labels.imported'),
      rejected: t('common.statuses.rejected'),
      cancelled: t('common.statuses.canceled'),
    }),
    [t],
  );

  const statusList = useMemo(
    () =>
      Object.keys(statusValueLabelMap).map((key) => ({ value: key, label: statusValueLabelMap[key] }) as QSelectItem),
    [statusValueLabelMap],
  );

  const dueList = useMemo(
    () =>
      [
        { label: t('common.labels.overdue'), value: 'true' },
        { label: t('common.labels.onTime'), value: 'false' },
      ] as QSelectItem[],
    [t],
  );

  const eventTypesMap = useMemo(() => {
    return (fetchedEventTypes ?? []).reduce(
      (acc, eventType) => {
        acc[eventType.lineage_id] = eventType;
        return acc;
      },
      {} as Record<string, EventType>,
    );
  }, [fetchedEventTypes]);

  const statusMap = useMemo(() => {
    return statusList.reduce(
      (acc, status) => {
        acc[status['value']] = status['label'];
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [statusList]);

  const productsMap = useMemo(() => {
    return (fetchedProducts ?? [])?.reduce(
      (acc, fetchedProduct) => {
        acc[fetchedProduct.id] = fetchedProduct;
        return acc;
      },
      {} as Record<string, Product>,
    );
  }, [fetchedProducts]);

  const rootCausesMap = useMemo(() => {
    return (fetchedRootCauses ?? [])?.reduce(
      (acc, fetchedRootCause) => {
        acc[fetchedRootCause.id] = fetchedRootCause;
        return acc;
      },
      {} as Record<string, RootCause>,
    );
  }, [fetchedRootCauses]);

  const activeUsers = useMemo(() => filterActiveUsers(fetchedUsers ?? []), [fetchedUsers]);

  const usersMap = useMemo(() => {
    return (fetchedUsers ?? [])?.reduce(
      (acc, fetchedUser) => {
        acc[fetchedUser.id.toString()] = fetchedUser;
        return acc;
      },
      {} as Record<string, User>,
    );
  }, [fetchedUsers]);

  const hideInaccessibleTags = isMultiSelectEventsListEnabled || isAdditionalEventListFiltersEnabled;

  const visibleTags = useMemo(
    () => (hideInaccessibleTags ? filterTags(userId, fetchedUsers ?? [], fetchedTags ?? []) : fetchedTags ?? []),
    [hideInaccessibleTags, userId, fetchedUsers, fetchedTags],
  );

  const tagsMap = useMemo(() => {
    return visibleTags.reduce(
      (acc, fetchedTag) => {
        acc[fetchedTag.id.toString()] = fetchedTag;
        return acc;
      },
      {} as Record<string, EventTag>,
    );
  }, [visibleTags]);

  const severitiesMap = useMemo(() => {
    return (fetchedEventMetadata?.severities ?? []).reduce(
      (acc, severity) => {
        acc[severity.id] = severity;
        return acc;
      },
      {} as Record<string, SeverityType>,
    );
  }, [fetchedEventMetadata]);

  const probabilitiesMap = useMemo(() => {
    return (fetchedEventMetadata?.probabilities ?? []).reduce(
      (acc, probability) => {
        acc[probability.id] = probability;
        return acc;
      },
      {} as Record<string, ProbabilityType>,
    );
  }, [fetchedEventMetadata]);

  const dueMap = useMemo(() => {
    return dueList.reduce(
      (acc, due) => {
        acc[due['value']] = due['label'];
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [dueList]);

  const activeEventTypeFilterRender = useCallback(
    (value: unknown) => (
      <ActiveEventTypeFilter
        value={value}
        eventTypesMap={eventTypesMap}
        isMultiSelect={isMultiSelectEventsListEnabled}
        isMultiSelectEventsListEnabled
      />
    ),
    [eventTypesMap, isMultiSelectEventsListEnabled],
  );

  const activeStatusFilterRender = useCallback(
    (value: unknown) => <ActiveStatusFilter value={value} statusMap={statusMap} />,
    [statusMap],
  );

  const activeProductFilterRender = useCallback(
    (value: unknown) => (
      <ActiveProductFilter
        value={value}
        productsMap={productsMap}
        isMultiSelect={isMultiSelectEventsListEnabled}
        isMultiSelectEventsListEnabled
      />
    ),
    [productsMap, isMultiSelectEventsListEnabled],
  );

  const activeRootCauseFilterRender = useCallback(
    (value: unknown) => (
      <ActiveRootCauseFilter
        value={value}
        rootCausesMap={rootCausesMap}
        isMultiSelect={isMultiSelectEventsListEnabled}
        isMultiSelectEventsListEnabled
      />
    ),
    [rootCausesMap, isMultiSelectEventsListEnabled],
  );

  const activeUserFilterRender = useCallback(
    (value: unknown) => (
      <ActiveUserFilter value={value} usersMap={usersMap} isMultiSelect={isMultiSelectEventsListEnabled} />
    ),
    [usersMap, isMultiSelectEventsListEnabled],
  );

  const activeCollaboratorFilterRender = useCallback(
    (value: unknown) => <ActiveUserFilter value={value} usersMap={usersMap} isMultiSelect />,
    [usersMap],
  );

  const activeTagFilterRender = useCallback(
    (value: unknown) => <ActiveTagFilter value={value} tagsMap={tagsMap} />,
    [tagsMap],
  );

  const activeDateFilterRender = useCallback((value: unknown) => <ActiveDateFilter value={value} />, []);

  const activeDueFilterRender = useCallback(
    (value: unknown) => <ActiveDueFilter value={value} dueMap={dueMap} />,
    [dueMap],
  );

  const activeSeverityFilterRender = useCallback(
    (value: unknown) => <ActiveSeverityFilter value={value} severitiesMap={severitiesMap} />,
    [severitiesMap],
  );

  const activeProbabilityFilterRender = useCallback(
    (value: unknown) => <ActiveProbabilityFilter value={value} probabilitiesMap={probabilitiesMap} />,
    [probabilitiesMap],
  );

  const activeRelatedRecordFilterRender = useCallback(
    (value: unknown) => <ActiveRelatedRecordFilter value={value} />,
    [],
  );

  const filterDefinitions: Filtering.FilterDefinitions = useMemo(() => {
    return {
      ...(isMultiSelectEventsListEnabled
        ? {
            event_types: {
              label: t('common.labels.eventType'),
              schema: Filtering.schemas.StringSchema().array().nullable(),
              activeRender: activeEventTypeFilterRender,
            },
          }
        : {
            event_type: {
              label: t('common.labels.eventType'),
              schema: Filtering.schemas.StringSchema().nullable(),
              activeRender: activeEventTypeFilterRender,
            },
          }),
      status: {
        label: t('common.labels.status'),
        schema: Filtering.schemas.StringSchema().array().nullable(),
        activeRender: activeStatusFilterRender,
        ...(isSavedViewsEnabled ? {} : { defaultValue: ['open', 'imported'] }),
      },
      ...(isMultiSelectEventsListEnabled
        ? {
            product_ids: {
              label: t('common.labels.product'),
              schema: Filtering.schemas.StringSchema().array().nullable(),
              activeRender: activeProductFilterRender,
            },
            rootcause_ids: {
              label: t('common.labels.rootCause'),
              schema: Filtering.schemas.StringSchema().array().nullable(),
              activeRender: activeRootCauseFilterRender,
            },
          }
        : {
            product_id: {
              label: t('common.labels.product'),
              schema: Filtering.schemas.StringSchema().nullable(),
              activeRender: activeProductFilterRender,
            },
            rootcause_id: {
              label: t('common.labels.rootCause'),
              schema: Filtering.schemas.StringSchema().nullable(),
              activeRender: activeRootCauseFilterRender,
            },
          }),
      filter_tags: {
        label: t('common.labels.tags'),
        schema: Filtering.schemas.StringSchema().array().nullable(),
        activeRender: activeTagFilterRender,
      },
      ...(isMultiSelectEventsListEnabled
        ? {
            owner_ids: {
              label: t('common.labels.owner'),
              schema: Filtering.schemas.StringSchema().array().nullable(),
              activeRender: activeUserFilterRender,
            },
          }
        : {
            owner_id: {
              label: t('common.labels.owner'),
              schema: Filtering.schemas.StringSchema().nullable(),
              activeRender: activeUserFilterRender,
            },
          }),
      ...(isEventCollaboratorsEnabled && isMultiSelectEventsListEnabled
        ? {
            collaborator_ids: {
              label: t('events.labels.collaborators'),
              schema: Filtering.schemas.StringSchema().array().nullable(),
              activeRender: activeCollaboratorFilterRender,
            },
          }
        : {}),
      created_at_start_date: {
        label: t('common.labels.createdAfter'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: activeDateFilterRender,
      },
      created_at_end_date: {
        label: t('common.labels.createdBefore'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: activeDateFilterRender,
      },
      ...(isAdditionalEventListFiltersEnabled
        ? {
            due_at_start_date: {
              label: t('common.labels.dueAfter'),
              schema: Filtering.schemas.StringSchema().nullable(),
              activeRender: activeDateFilterRender,
            },
            due_at_end_date: {
              label: t('common.labels.dueBefore'),
              schema: Filtering.schemas.StringSchema().nullable(),
              activeRender: activeDateFilterRender,
            },
            resolved_at_start_date: {
              label: t('common.labels.resolvedAfter'),
              schema: Filtering.schemas.StringSchema().nullable(),
              activeRender: activeDateFilterRender,
            },
            resolved_at_end_date: {
              label: t('common.labels.resolvedBefore'),
              schema: Filtering.schemas.StringSchema().nullable(),
              activeRender: activeDateFilterRender,
            },
            severity_ids: {
              label: t('common.labels.severity'),
              schema: Filtering.schemas.StringSchema().array().nullable(),
              activeRender: activeSeverityFilterRender,
            },
            probability_ids: {
              label: t('common.labels.probability'),
              schema: Filtering.schemas.StringSchema().array().nullable(),
              activeRender: activeProbabilityFilterRender,
            },
            related_records: {
              label: t('common.labels.relatedRecords'),
              schema: Filtering.schemas.StringSchema().array().nullable(),
              activeRender: activeRelatedRecordFilterRender,
            },
          }
        : {}),
      is_overdue: {
        label: t('common.labels.due'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: activeDueFilterRender,
      },
    };
  }, [
    activeEventTypeFilterRender,
    activeStatusFilterRender,
    activeProductFilterRender,
    activeRootCauseFilterRender,
    activeTagFilterRender,
    activeUserFilterRender,
    activeCollaboratorFilterRender,
    activeDateFilterRender,
    activeDueFilterRender,
    activeSeverityFilterRender,
    activeProbabilityFilterRender,
    activeRelatedRecordFilterRender,
    isMultiSelectEventsListEnabled,
    isEventCollaboratorsEnabled,
    isAdditionalEventListFiltersEnabled,
    isSavedViewsEnabled,
    t,
  ]);

  const disableTextColumnSort = isMultiSelectEventsListEnabled || isAdditionalEventListFiltersEnabled;
  // Only the medtech list endpoint returns collaborators; the QEBE one would leave the column empty.
  const isCollaboratorsColumnEnabled = isEventCollaboratorsEnabled && isMultiSelectEventsListEnabled;

  const columns = useMemo(
    () =>
      getEventTableColumns({
        t,
        localiseDate,
        statusValueLabelMap,
        disableTextColumnSort,
        isConfigurableColumnsEnabled,
        isCollaboratorsColumnEnabled,
      }),
    [
      localiseDate,
      statusValueLabelMap,
      disableTextColumnSort,
      isConfigurableColumnsEnabled,
      isCollaboratorsColumnEnabled,
      t,
    ],
  );

  const fetchEvents = async ({
    pageParam,
  }: {
    pageParam: DataProvider.PageParams;
  }): Promise<DataProvider.TDataWithCount<ListQualityEvent>> => {
    const filterObjects = pageParam.filters ?? {};
    const filters = Object.fromEntries(Object.entries(filterObjects).map(([k, v]) => [k, v.value]));

    const searchTerm = pageParam.searchTerm;
    const pagination = pageParam.pagination;
    const sorting = pageParam.sorting;

    // The URL/storage holds `related_records` as a `string[]` of QRIs, but the wire payload
    // expects reference objects — decode each QRI before POSTing.
    const relatedRecordQris = filters.related_records as string[] | undefined;
    const relatedRecords: RelatedRecordRef[] | undefined = relatedRecordQris?.length
      ? relatedRecordQris.map((qri) => {
          const { domain, type, id, version_id } = decodeQri(qri);
          return { domain, type, id, ...(version_id ? { version_id } : {}) };
        })
      : undefined;

    const finalParams: QualityEventsGet = {
      ...filters,
      page_number: pagination ? pagination.pageIndex + 1 : 1,
      page_size: pagination ? pagination.pageSize : 15,
      created_at_start_date: getUtcStartOfDay(filters.created_at_start_date as string | undefined, tz),
      created_at_end_date: getUtcEndOfDay(filters.created_at_end_date as string | undefined, tz),
      // due_at is a calendar date (see due_date / DueColumnV2), not an instant — filter on
      // UTC day boundaries, without localising to the viewer tz (unlike created_at/resolved_at).
      due_at_start_date: getUtcStartOfDay(filters.due_at_start_date as string | undefined),
      due_at_end_date: getUtcEndOfDay(filters.due_at_end_date as string | undefined),
      resolved_at_start_date: getUtcStartOfDay(filters.resolved_at_start_date as string | undefined, tz),
      resolved_at_end_date: getUtcEndOfDay(filters.resolved_at_end_date as string | undefined, tz),
      related_records: relatedRecords,
    };

    if (searchTerm) {
      finalParams['term'] = searchTerm;
    }

    if (sorting?.column) {
      const prefix = sorting.descending ? '-' : '';
      finalParams['order_by'] = `${prefix}${sorting.column}`;
    }

    const results = isMultiSelectEventsListEnabled
      ? await eventsApi.getAllEventsMtbe(companyId, finalParams)
      : await eventsApi.getAllEvents(companyId, finalParams);

    return {
      itemCount: results?.meta.page.total_items,
      data: results?.data ?? [],
    };
  };

  return (
    <>
      <CumulativeSearchParamsProvider>
        <PaginationProvider>
          <Filtering.FilterProvider
            searchTermKey="term"
            definitions={filterDefinitions}
            persistanceKey={eventsTablePersistanceKey}
          >
            <Sorting.DefaultSortingProvider sortByKey="order_by" persistenceKey={eventsTablePersistanceKey}>
              <SavedViews.SavedViewsProvider
                medtechClient={medtechApi}
                tableKey={eventsListSavedViewsTableKey}
                enabled={isSavedViewsEnabled}
              >
                <DataProvider.Remote
                  queryFn={fetchEvents}
                  queryKey={['getEvents', companyId, isMultiSelectEventsListEnabled]}
                  queryParamKeys={{
                    searchTerm: 'term',
                    sortBy: 'order_by',
                    pageSize: 'limit',
                    pageIndex: 'offset',
                  }}
                >
                  <QDataTable
                    columns={columns}
                    enableSorting
                    data-cy="events-table"
                    getRowId={(row) => row.id.toString()}
                    itemCountCap={SEARCH_TOTAL_ITEMS_CAP}
                  >
                    <Filtering.FormContent>
                      <QStack spacing={4} p={1} pb={2} w="624px">
                        <QStack spacing={4} direction="row">
                          <EventTypeFilter
                            isLoadingEventTypes={isLoadingEventTypes}
                            eventTypes={fetchedEventTypes ?? []}
                            isMultiSelect={isMultiSelectEventsListEnabled}
                            isMultiSelectEventsListEnabled
                          />
                          <StatusFilter statusList={statusList} />
                        </QStack>
                        <QStack spacing={4} direction="row">
                          <ProductFilter
                            isLoadingProducts={isLoadingProducts}
                            products={fetchedProducts ?? []}
                            isMultiSelect={isMultiSelectEventsListEnabled}
                            isAdditionalEventListFiltersEnabled={isAdditionalEventListFiltersEnabled}
                            isMultiSelectEventsListEnabled
                          />
                          <RootCauseFilter
                            isLoadingRootCauses={isLoadingRootCauses}
                            rootCauses={fetchedRootCauses ?? []}
                            isMultiSelect={isMultiSelectEventsListEnabled}
                            isAdditionalEventListFiltersEnabled={isAdditionalEventListFiltersEnabled}
                            isMultiSelectEventsListEnabled
                          />
                        </QStack>
                        {isEventCollaboratorsEnabled && isMultiSelectEventsListEnabled ? (
                          <>
                            <QStack spacing={4} direction="row">
                              <UserFilter
                                isLoadingUsers={isLoadingUsers}
                                users={fetchedUsers ?? []}
                                placeholderText={t('common.filters.filterByOwner')}
                                isMultiSelect={isMultiSelectEventsListEnabled}
                              />
                              <UserFilter
                                isLoadingUsers={isLoadingUsers}
                                users={activeUsers}
                                placeholderText={t('common.filters.filterByCollaborator')}
                                label={t('events.labels.collaborators')}
                                isMultiSelect
                                multiSelectName="collaborator_ids"
                                dataCy="collaborator-filter"
                              />
                            </QStack>

                            <QStack spacing={4} direction="row">
                              <TagFilter
                                isLoadingTags={isLoadingTags}
                                tags={visibleTags}
                                isAdditionalEventListFiltersEnabled={isAdditionalEventListFiltersEnabled}
                              />
                            </QStack>
                          </>
                        ) : (
                          <QStack spacing={4} direction="row">
                            <UserFilter
                              isLoadingUsers={isLoadingUsers}
                              users={fetchedUsers ?? []}
                              placeholderText={t('common.filters.filterByOwner')}
                              isMultiSelect={isMultiSelectEventsListEnabled}
                            />
                            <TagFilter
                              isLoadingTags={isLoadingTags}
                              tags={visibleTags}
                              isAdditionalEventListFiltersEnabled={isAdditionalEventListFiltersEnabled}
                            />
                          </QStack>
                        )}
                        <QStack spacing={4} direction="row">
                          <DateFilter
                            name="created_at_start_date"
                            label={t('common.labels.createdAfter')}
                            placeholder={FilterByCreatedDate}
                            dataCy="created-after-filter"
                          />
                          <DateFilter
                            name="created_at_end_date"
                            label={t('common.labels.createdBefore')}
                            placeholder={FilterByCreatedDate}
                            dataCy="created-before-filter"
                          />
                        </QStack>
                        {isAdditionalEventListFiltersEnabled && (
                          <>
                            <QStack spacing={4} direction="row">
                              <DateFilter
                                name="due_at_start_date"
                                label={t('common.labels.dueAfter')}
                                placeholder={FilterByDueDate}
                                dataCy="due-after-filter"
                              />
                              <DateFilter
                                name="due_at_end_date"
                                label={t('common.labels.dueBefore')}
                                placeholder={FilterByDueDate}
                                dataCy="due-before-filter"
                              />
                            </QStack>
                            <QStack spacing={4} direction="row">
                              <DateFilter
                                name="resolved_at_start_date"
                                label={t('common.labels.resolvedAfter')}
                                placeholder={FilterByResolvedDate}
                                dataCy="resolved-after-filter"
                              />
                              <DateFilter
                                name="resolved_at_end_date"
                                label={t('common.labels.resolvedBefore')}
                                placeholder={FilterByResolvedDate}
                                dataCy="resolved-before-filter"
                              />
                            </QStack>
                            <QStack spacing={4} direction="row">
                              <SeverityFilter
                                isLoadingSeverities={isLoadingEventMetadata}
                                severities={fetchedEventMetadata?.severities ?? []}
                              />
                              <ProbabilityFilter
                                isLoadingProbabilities={isLoadingEventMetadata}
                                probabilities={fetchedEventMetadata?.probabilities ?? []}
                              />
                            </QStack>
                            <QStack spacing={4} direction="row">
                              <RelatedRecordsFilter />
                            </QStack>
                          </>
                        )}
                        <QStack spacing={4} direction="row" width="300px">
                          <DueFilter dueList={dueList} />
                        </QStack>
                      </QStack>
                    </Filtering.FormContent>
                    <QEmptyState title={t('common.tables.noMatchingEventsFound')} data-cy="events-table-empty-state" />
                  </QDataTable>
                </DataProvider.Remote>
              </SavedViews.SavedViewsProvider>
            </Sorting.DefaultSortingProvider>
          </Filtering.FilterProvider>
        </PaginationProvider>
      </CumulativeSearchParamsProvider>
    </>
  );
};

export default EventTable;
